/**
 * The one place the UI turns times into text. Everything here uses the IANA
 * zone `Europe/London`, so BST/GMT and the clock-change days are handled by the
 * platform tz database and never by hand-rolled offsets.
 *
 * Three kinds of value, three different treatments:
 *   1. **Instants** (`…Z`, `…+01:00`, epoch ms, `Date`) → converted to UK time.
 *   2. **Zone-less date-time strings** (`2026-10-12 11:20:00`) → read as **UTC**
 *      everywhere in this module (the Trino/Athena `timestamp` convention for
 *      `occurred_at` and friends), then converted to UK time.
 *      Zone-less values that are already UK wall clock (Palletways POD/PDT) go
 *      through `lib/calc/clock` instead, which shows them as written. The two
 *      rules give answers an hour apart in BST, so this module deliberately does
 *      not re-export clock.ts's formatters — pick the module by what the value is.
 *   3. **Date-only keys** (`2026-10-12`) → a calendar day, never an instant.
 *      Use the `*Ymd` helpers: they format at noon UTC with `timeZone: 'UTC'`
 *      so no viewer zone can move the date.
 *
 * Do not call `toLocaleDateString`/`toLocaleTimeString`/`getHours`/`getUTC*` on
 * dates in UI code — `ukTime.lint.test.ts` fails if new uses appear.
 *
 * Display only: this module never changes which instant was stored or fetched.
 */
import { formatUkFullDateTime, ukClockParts, OPERATING_TIME_ZONE } from '../lib/calc/clock';
import { shiftYmd, daysBetweenYmd, todayYmdInZone } from '../lib/calc/dates';
import { UNKNOWN_CELL } from './uiText';

export { shiftYmd, daysBetweenYmd };

export const UK_TIME_ZONE = OPERATING_TIME_ZONE;

type TimeInput = string | number | Date | null | undefined;

const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const NAIVE_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?$/;

/** Parse an instant. Zone-less strings are read as UTC (see {@link formatUkTableDateTime}). */
export function parseInstant(value: TimeInput): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  let s = value.trim();
  if (!s || DATE_ONLY.test(s)) return null;
  if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
  if (!ZONED.test(s)) s = `${s}Z`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `BST` or `GMT` for the instant. */
export function ukZoneAbbr(value: TimeInput): string {
  const d = parseInstant(value);
  if (!d) return '';
  const part = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TIME_ZONE, timeZoneName: 'short' })
    .formatToParts(d)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? '';
}

/**
 * `2026-07-30 13:10:28` in UK time — the fixed-width form used in tables.
 * Zone-less input is taken as UTC (the backend's Trino/Athena convention).
 */
export function formatUkTableDateTime(value: TimeInput, fallback = ''): string {
  const d = parseInstant(value);
  const p = d ? ukClockParts(d) : null;
  if (!p) return fallback;
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
}

/** `12 Oct 2026, 01:30 BST` — the zone suffix disambiguates the repeated 25 Oct hour. */
export function formatUkDateTimeWithZone(value: TimeInput, fallback = UNKNOWN_CELL): string {
  const d = parseInstant(value);
  if (!d) return fallback;
  return `${formatUkFullDateTime(d, fallback)} ${ukZoneAbbr(d)}`;
}

/** `HH:mm` (or `HH:mm:ss` with `seconds`) in UK time. */
export function formatUkTime(
  value: TimeInput,
  opts: { seconds?: boolean } = {},
  fallback = UNKNOWN_CELL,
): string {
  const d = parseInstant(value);
  const p = d ? ukClockParts(d) : null;
  if (!p) return fallback;
  return opts.seconds ? `${p.hour}:${p.minute}:${p.second}` : `${p.hour}:${p.minute}`;
}

/** `dd/mm/yyyy, hh:mm:ss` in UK time (24h). */
export function formatUkNumericDateTime(value: TimeInput, fallback = UNKNOWN_CELL): string {
  const d = parseInstant(value);
  const p = d ? ukClockParts(d) : null;
  if (!p) return fallback;
  return `${p.day}/${p.month}/${p.year}, ${p.hour}:${p.minute}:${p.second}`;
}

/** UK calendar day (`YYYY-MM-DD`) of an instant. */
export function ukYmd(value: TimeInput): string | null {
  const d = parseInstant(value);
  return d ? todayYmdInZone(UK_TIME_ZONE, d) : null;
}

/** Today's UK calendar day. */
export function ukTodayYmd(now: Date = new Date()): string {
  return todayYmdInZone(UK_TIME_ZONE, now);
}

/** "5 min ago" / "in 2 h" / "yesterday" relative to `now`, day steps counted on the UK calendar. */
export function formatUkRelative(value: TimeInput, now: Date = new Date(), fallback = UNKNOWN_CELL): string {
  const d = parseInstant(value);
  if (!d) return fallback;
  const diffMs = d.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const future = diffMs > 0;
  const wrap = (s: string) => (future ? `in ${s}` : `${s} ago`);
  if (abs < 45_000) return 'just now';
  if (abs < 3_600_000) return wrap(`${Math.max(1, Math.round(abs / 60_000))} min`);
  if (abs < 86_400_000) return wrap(`${Math.round(abs / 3_600_000)} h`);
  const days = daysBetweenYmd(todayYmdInZone(UK_TIME_ZONE, now), todayYmdInZone(UK_TIME_ZONE, d)) ?? 0;
  if (days === -1) return 'yesterday';
  if (days === 1) return 'tomorrow';
  return wrap(`${Math.abs(days)} d`);
}

// ---- date-only keys -------------------------------------------------------

export function isDateOnly(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY.test(value.trim());
}

type YmdFormat = Pick<Intl.DateTimeFormatOptions, 'weekday' | 'day' | 'month' | 'year'>;

/**
 * Format a `YYYY-MM-DD` calendar day without ever shifting it. Accepts a longer
 * string and uses its first ten characters (the date part as written).
 * Returns `fallback` if the input does not start with a valid date.
 */
export function formatYmd(
  ymd: string | null | undefined,
  options: YmdFormat = { day: 'numeric', month: 'short', year: 'numeric' },
  fallback = UNKNOWN_CELL,
): string {
  const key = ymd?.trim().slice(0, 10) ?? '';
  if (!DATE_ONLY.test(key)) return fallback;
  const d = new Date(`${key}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' }).format(d);
}

/** Short weekday (`Mon`) of a calendar day. */
export function ymdWeekday(ymd: string, fallback = ''): string {
  return formatYmd(ymd, { weekday: 'short' }, fallback);
}

// ---- wall clock -> instant -----------------------------------------------

export type WallClockResolution =
  | { kind: 'unique'; instant: Date }
  | { kind: 'ambiguous'; earlier: Date; later: Date }
  | { kind: 'nonexistent' }
  | { kind: 'invalid' };

/**
 * Interpret a zone-less wall-clock string (`2026-10-25 01:30:00`) as UK local
 * time. On the autumn change day the 01:00–02:00 hour happens twice
 * (`ambiguous`, with both candidate instants); on the spring change day
 * 01:00–02:00 never happens (`nonexistent`). Callers must record ambiguity
 * rather than silently picking one.
 */
export function resolveUkWallClock(naive: string): WallClockResolution {
  const m = naive.trim().match(NAIVE_DATETIME);
  if (!m) return { kind: 'invalid' };
  const [y, mo, d, h, mi, s] = [m[1], m[2], m[3], m[4], m[5], m[6] ?? '0'].map(Number);
  const asUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  if (new Date(asUtc).getUTCMonth() !== mo - 1) return { kind: 'invalid' };
  // London is UTC+0 (GMT) or UTC+1 (BST): try both offsets, keep those that round-trip.
  const matches = [60, 0]
    .map((offsetMin) => new Date(asUtc - offsetMin * 60_000))
    .filter((cand) => {
      const p = ukClockParts(cand);
      return (
        p !== null &&
        Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) === asUtc
      );
    });
  if (matches.length === 0) return { kind: 'nonexistent' };
  if (matches.length === 1) return { kind: 'unique', instant: matches[0] };
  return { kind: 'ambiguous', earlier: matches[0], later: matches[1] };
}

// ---- values whose zone label is not trustworthy ---------------------------

/**
 * Fallback for an Aquarius time when zeecore sent no real instant (an older or
 * cached response). Aquarius history is stored as UK wall clock labelled `Z`
 * (zeecore/docs/truth-layer/aquarius_time_basis_cutover.md), so the stored
 * clock digits are shown as written, with no zone conversion. Prefer the
 * `*_instant` fields zeecore now sends.
 *
 * `YYYY-MM-DD HH:mm:ss` (`{ numeric: true }` → `dd/mm/yyyy, HH:mm:ss`; `{ hm: true }` → `HH:mm`); `fallback` if unparseable.
 */
export function formatSuppliedClock(
  value: string | null | undefined,
  opts: { hm?: boolean; numeric?: boolean } = {},
  fallback = UNKNOWN_CELL,
): string {
  const m = value?.trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return fallback;
  if (opts.hm) return `${m[4]}:${m[5]}`;
  const time = `${m[4]}:${m[5]}:${m[6] ?? '00'}`;
  return opts.numeric ? `${m[3]}/${m[2]}/${m[1]}, ${time}` : `${m[1]}-${m[2]}-${m[3]} ${time}`;
}

// ---- calendar grids --------------------------------------------------------

export interface MonthGrid {
  year: number;
  /** 0-based month. */
  month: number;
  daysInMonth: number;
  /** Empty cells before the 1st on a Monday-first grid. */
  leading: number;
  /** `October 2026` */
  label: string;
}

/** The month `monthOffset` months from the current UK month, as a Monday-first grid. */
export function ukMonthGrid(monthOffset = 0, now: Date = new Date()): MonthGrid {
  const [y, m] = ukTodayYmd(now).split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1 + monthOffset, 1));
  const year = first.getUTCFullYear();
  const month = first.getUTCMonth();
  return {
    year,
    month,
    daysInMonth: new Date(Date.UTC(year, month + 1, 0)).getUTCDate(),
    leading: (first.getUTCDay() + 6) % 7,
    label: formatYmd(`${year}-${String(month + 1).padStart(2, '0')}-01`, { month: 'long', year: 'numeric' }),
  };
}
